import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    FaChevronDown,
    FaChevronRight,
    FaDatabase,
    FaDownload,
    FaEye,
    FaInfoCircle,
    FaKey,
    FaLink,
    FaSearch,
    FaSync,
    FaTable,
    FaTerminal,
    FaTimes
} from 'react-icons/fa';
import styles from '../styles/Datamart.module.css';

export default function ResponsiveDictionary({
    databases,
    currentDb,
    currentTables,
    currentTable,
    selectedDbName,
    searchQuery,
    hideSystemTables,
    loading,
    hasDatabases,
    onSelectDb,
    onSelectTable,
    onSearchChange,
    onHideSystemTablesChange,
    onRefresh,
    onExport,
    onOpenSample,
    onSendToQuery
}) {
    const [isSchemaModalOpen, setIsSchemaModalOpen] = useState(false);
    const dialogRef = useRef(null);
    const closeButtonRef = useRef(null);
    const triggerButtonRef = useRef(null);
    const restoreFocusRef = useRef(false);

    const closeSchemaModal = useCallback((restoreFocus = true) => {
        restoreFocusRef.current = restoreFocus;
        setIsSchemaModalOpen(false);
    }, []);

    useEffect(() => {
        if (!isSchemaModalOpen) {
            if (restoreFocusRef.current) {
                triggerButtonRef.current?.focus();
            }
            return undefined;
        }

        restoreFocusRef.current = true;
        closeButtonRef.current?.focus();
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const handleDialogKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                closeSchemaModal();
                return;
            }

            if (event.key !== 'Tab') return;

            const focusableElements = dialogRef.current?.querySelectorAll(
                'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
            );
            if (!focusableElements?.length) {
                event.preventDefault();
                dialogRef.current?.focus();
                return;
            }

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            if (event.shiftKey && document.activeElement === firstElement) {
                event.preventDefault();
                lastElement.focus();
            } else if (!event.shiftKey && document.activeElement === lastElement) {
                event.preventDefault();
                firstElement.focus();
            }
        };

        document.addEventListener('keydown', handleDialogKeyDown);
        return () => {
            document.removeEventListener('keydown', handleDialogKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [closeSchemaModal, isSchemaModalOpen]);

    return (
        <div className={styles.responsiveDictionary}>
            <div className={styles.responsiveDictionaryActions}>
                <button
                    type="button"
                    className={styles.secondaryActionBtn}
                    onClick={onRefresh}
                    disabled={loading}
                    title="Recargar catálogo de bases de datos"
                >
                    <FaSync className={loading ? styles.spinIcon : ''} aria-hidden="true" />
                    Actualizar
                </button>
                <button
                    type="button"
                    className={styles.secondaryActionBtn}
                    onClick={onExport}
                    disabled={!hasDatabases}
                    title="Exportar diccionario en formato JSON"
                >
                    <FaDownload aria-hidden="true" /> Exportar JSON
                </button>
            </div>

            <div className={styles.responsiveDatabaseSelector}>
                <label className={styles.responsiveDbLabel} htmlFor="datamart-responsive-database">
                    Base de datos
                </label>
                <div className={styles.responsiveDbSelectWrap}>
                    <FaDatabase className={styles.responsiveDbIcon} aria-hidden="true" />
                    <select
                        id="datamart-responsive-database"
                        className={styles.responsiveDbSelect}
                        value={databases.some((database) => database.name === selectedDbName) ? selectedDbName : ''}
                        onChange={(event) => {
                            if (isSchemaModalOpen) closeSchemaModal(false);
                            onSelectDb(event.target.value);
                        }}
                        disabled={databases.length === 0}
                    >
                        {databases.length === 0 && <option value="">No hay bases de datos disponibles</option>}
                        {databases.map((database) => (
                            <option key={database.name} value={database.name}>
                                {database.name}
                            </option>
                        ))}
                    </select>
                    <FaChevronDown className={styles.responsiveDbChevron} aria-hidden="true" />
                </div>
            </div>

            <div className={styles.filterBar}>
                <div className={styles.searchBox}>
                    <FaSearch className={styles.searchIcon} aria-hidden="true" />
                    <input
                        type="search"
                        className={styles.searchInput}
                        placeholder="Buscar base, tabla o columna..."
                        value={searchQuery}
                        onChange={(event) => onSearchChange(event.target.value)}
                        aria-label="Buscar en el diccionario"
                    />
                    {searchQuery && (
                        <button
                            type="button"
                            className={styles.clearSearchBtn}
                            onClick={() => onSearchChange('')}
                            aria-label="Limpiar búsqueda"
                        >
                            <FaTimes aria-hidden="true" />
                        </button>
                    )}
                </div>
                <label className={styles.checkboxLabel}>
                    <input
                        type="checkbox"
                        checked={hideSystemTables}
                        onChange={(event) => onHideSystemTablesChange(event.target.checked)}
                    />
                    <span>Ocultar tablas de sistema</span>
                </label>
            </div>

            {currentDb ? (
                <>
                    <section className={styles.responsiveTablesSection} aria-labelledby="datamart-responsive-tables-heading">
                        <div className={styles.responsiveSectionHeader}>
                            <h3 id="datamart-responsive-tables-heading">Tablas</h3>
                            <span>{currentTables.length} disponibles</span>
                        </div>

                        {currentTables.length === 0 ? (
                            <div className={styles.responsiveEmptyState}>
                                <FaInfoCircle aria-hidden="true" />
                                <span>No hay tablas creadas en esta base de datos.</span>
                            </div>
                        ) : (
                            <div className={styles.responsiveTableGrid}>
                                {currentTables.map((table) => {
                                    const isActive = table.name === currentTable?.name;
                                    const query = searchQuery.trim().toLowerCase();
                                    const matchesQuery = query && (
                                        table.name.toLowerCase().includes(query)
                                        || (table.columns || []).some((column) => column.name.toLowerCase().includes(query))
                                    );

                                    return (
                                        <button
                                            key={table.name}
                                            type="button"
                                            className={`${styles.responsiveTableCard} ${isActive ? styles.responsiveTableCardActive : ''} ${matchesQuery ? styles.highlightMatch : ''}`}
                                            onClick={(event) => {
                                                triggerButtonRef.current = event.currentTarget;
                                                onSelectTable(table.name);
                                                setIsSchemaModalOpen(true);
                                            }}
                                            aria-pressed={isActive}
                                        >
                                            <span className={styles.responsiveTableIcon}>
                                                <FaTable aria-hidden="true" />
                                            </span>
                                            <span className={styles.responsiveTableInfo}>
                                                <span className={styles.responsiveTableName}>{table.name}</span>
                                                <span className={styles.responsiveTableMeta}>
                                                    {(table.columns_count ?? table.columns?.length ?? 0)} columnas · {(table.row_count ?? 0)} filas
                                                </span>
                                            </span>
                                            <FaChevronRight className={styles.responsiveTableChevron} aria-hidden="true" />
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </section>

                    {isSchemaModalOpen && currentDb && currentTable && (
                        <div
                            className={`${styles.modalOverlay} ${styles.responsiveSchemaOverlay}`}
                            data-testid="responsive-schema-backdrop"
                            onClick={(event) => {
                                if (event.target === event.currentTarget) closeSchemaModal();
                            }}
                        >
                            <section
                                ref={dialogRef}
                                className={`${styles.modalCard} ${styles.responsiveSchemaModal}`}
                                role="dialog"
                                aria-modal="true"
                                aria-labelledby="datamart-responsive-schema-title"
                                tabIndex={-1}
                            >
                                <header className={`${styles.modalHeader} ${styles.responsiveSchemaHeader}`}>
                                    <div className={styles.responsiveSchemaHeading}>
                                        <FaTable aria-hidden="true" />
                                        <div className={styles.responsiveSchemaTitleGroup}>
                                            <h3 className={styles.modalTitle} id="datamart-responsive-schema-title">
                                                Esquema de {currentDb.name}.{currentTable.name}
                                            </h3>
                                            <div className={styles.responsiveTableStats}>
                                                <span className={styles.pillRows}>{currentTable.row_count} registros</span>
                                                <span className={styles.pillCols}>{currentTable.columns_count} columnas</span>
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        ref={closeButtonRef}
                                        type="button"
                                        className={styles.closeBtn}
                                        onClick={() => closeSchemaModal()}
                                        aria-label="Cerrar esquema de tabla"
                                    >
                                        <FaTimes aria-hidden="true" />
                                    </button>
                                </header>

                                <div className={`${styles.modalBody} ${styles.responsiveSchemaBody}`}>
                                    {currentTable.description && (
                                        <p className={styles.responsiveSchemaDescription}>{currentTable.description}</p>
                                    )}

                                    <div className={styles.responsiveDetailActions}>
                                        <button
                                            type="button"
                                            className={styles.actionBtnSecondary}
                                            onClick={() => {
                                                closeSchemaModal(false);
                                                onOpenSample(currentDb.name, currentTable.name);
                                            }}
                                        >
                                            <FaEye aria-hidden="true" /> Muestra de Datos
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.actionBtnPrimary}
                                            onClick={() => {
                                                closeSchemaModal(false);
                                                onSendToQuery(currentDb.name, currentTable);
                                            }}
                                        >
                                            <FaTerminal aria-hidden="true" /> Abrir en Consulta
                                        </button>
                                    </div>

                                    {(currentTable.columns || []).length > 0 ? (
                                        <div
                                            className={styles.responsiveSchemaCards}
                                            tabIndex={0}
                                            role="list"
                                            aria-label={`Columnas de ${currentDb.name}.${currentTable.name}`}
                                        >
                                            {currentTable.columns.map((column) => {
                                                const isColumnMatch = searchQuery.trim()
                                                    && column.name.toLowerCase().includes(searchQuery.trim().toLowerCase());

                                                return (
                                                    <article
                                                        key={column.name}
                                                        className={`${styles.responsiveColumnCard} ${isColumnMatch ? styles.colHighlight : ''}`}
                                                        role="listitem"
                                                        aria-label={`Columna ${column.name}`}
                                                    >
                                                        <div className={styles.responsiveColumnHeader}>
                                                            <div className={styles.responsiveColumnIdentity}>
                                                                <h4>{column.name}</h4>
                                                                {column.is_primary_key && (
                                                                    <span className={styles.badgePk} title="Llave primaria">
                                                                        <FaKey aria-hidden="true" /> PK
                                                                    </span>
                                                                )}
                                                                {column.is_foreign_key && (
                                                                    <span
                                                                        className={styles.badgeFk}
                                                                        title={column.foreign_key_target ? `Referencia a: ${column.foreign_key_target}` : 'Llave foránea'}
                                                                    >
                                                                        <FaLink aria-hidden="true" /> FK
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <span className={styles.typeCodeBadge}>{column.type}</span>
                                                        </div>

                                                        <dl className={styles.responsiveColumnMeta}>
                                                            <div>
                                                                <dt>Restricciones</dt>
                                                                <dd>
                                                                    {column.nullable
                                                                        ? <span className={styles.badgeNull}>NULL</span>
                                                                        : <span className={styles.badgeNotNull}>NOT NULL</span>}
                                                                    {column.foreign_key_target && (
                                                                        <span className={styles.fkTargetNotice}>
                                                                            → {column.foreign_key_target}
                                                                        </span>
                                                                    )}
                                                                </dd>
                                                            </div>
                                                            <div>
                                                                <dt>Valor predeterminado</dt>
                                                                <dd>
                                                                    {column.default
                                                                        ? <code className={styles.codeSnippet}>{column.default}</code>
                                                                        : <span className={styles.mutedText}>—</span>}
                                                                </dd>
                                                            </div>
                                                            <div className={styles.responsiveColumnDescription}>
                                                                <dt>Descripción / propósito</dt>
                                                                <dd>
                                                                    {column.description || (
                                                                        <span className={styles.mutedText}>
                                                                            {column.is_primary_key
                                                                                ? 'Identificador único del registro.'
                                                                                : 'Campo de datos de la entidad.'}
                                                                        </span>
                                                                    )}
                                                                </dd>
                                                            </div>
                                                        </dl>
                                                    </article>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className={styles.responsiveEmptyState}>
                                            <FaInfoCircle aria-hidden="true" />
                                            <span>Esta tabla no tiene columnas documentadas.</span>
                                        </div>
                                    )}
                                </div>
                            </section>
                        </div>
                    )}
                </>
            ) : (
                <div className={styles.responsiveEmptyState}>
                    <FaDatabase aria-hidden="true" />
                    <span>No se encontraron bases de datos coincidentes con la búsqueda.</span>
                </div>
            )}
        </div>
    );
}
